import type { ClimateFactor, CountryProfile, HealthcareFactor, Rating, SafetyFactor } from './types';

/** The six scored factors and what each is worth out of 100. */
export const FACTOR_WEIGHTS = {
  /*
   * Tax and visa treatment and healthcare carry the most. Tax and visa
   * decides whether retiring there is possible at all, and the gap between a
   * flat 7% on foreign pensions and ordinary taxation is real money every
   * year. Healthcare is what you use most once you have arrived, and more so
   * every year you stay.
   * Property, climate and safety sit in the middle. Climate scores the
   * narrowest question: not whether the weather suits you (there's no
   * universal answer to that) but whether it's a stable one to spend a few
   * decades in, see `climateRating`. Safety covers everyday crime, organised
   * crime and corruption, see `safetyRating`.
   * State Pension uprating carries the least: the app models your pot, not
   * the State Pension, so a frozen one is a real but secondary cost here.
   *
   * Cost of living is deliberately absent. It is already answered far more
   * precisely by the surplus, which weighs each country's budget against your
   * own projected income in your own currency; scoring it here as well would
   * count the same fact twice.
   */
  expatTaxTreatment: 25,
  healthcare: 25,
  propertyRights: 15,
  climate: 15,
  safety: 15,
  pensionUprating: 5,
} as const;

export type ScoredFactor = keyof typeof FACTOR_WEIGHTS;

const SCORED_FACTORS = Object.keys(FACTOR_WEIGHTS) as ScoredFactor[];

/** A neutral rating earns half its factor's weight. */
const RATING_SHARE: Record<Rating, number> = {
  favorable: 1,
  neutral: 0.5,
  unfavorable: 0,
};

/**
 * Healthcare is the one factor we build rather than assert, because the single
 * word "favourable" was hiding three different questions that pull in opposite
 * directions: how good the care is, whether an arriving retiree can actually
 * get into the public system, and what the private alternative costs. Thailand
 * scored the same as Spain on a criterion Thailand answers "no public access"
 * to; the US was marked down for a waiting period that Canada and Australia
 * were forgiven. Splitting the question fixes both.
 *
 * Quality is the only third with a citation behind it — Numbeo's index, which
 * measures what residents say about the care they receive. The other two are
 * editorial, but they are at least stated plainly enough to be argued with.
 */
const QUALITY_POINTS = (index: number): number => (index >= 75 ? 2 : index >= 60 ? 1 : 0);

const ACCESS_POINTS: Record<HealthcareFactor['publicAccess'], number> = {
  open: 1,
  'after-wait': 0,
  closed: -1,
};

const PRIVATE_POINTS: Record<HealthcareFactor['privateCare'], number> = {
  affordable: 1,
  moderate: 0,
  expensive: -1,
};

/** -2 to +4, banded into the same three ratings every other factor uses. */
export function healthcareScore(h: HealthcareFactor): number {
  return QUALITY_POINTS(h.qualityIndex) + ACCESS_POINTS[h.publicAccess] + PRIVATE_POINTS[h.privateCare];
}

export function healthcareRating(h: HealthcareFactor): Rating {
  const points = healthcareScore(h);
  if (points >= 3) return 'favorable';
  if (points >= 1) return 'neutral';
  return 'unfavorable';
}

/**
 * Three independent -1/0/+1 signals, each thirds of its own scale, added
 * together: local environmental quality (Numbeo Pollution Index, lower is
 * better), historical exposure to extreme weather (Germanwatch's Climate
 * Risk Index, long-term rank out of 174 — a low rank number means MORE
 * affected, so it inverts), and whether the country is actually doing
 * anything about it (Climate Change Performance Index rank out of 67, low
 * rank is better). A country not tracked by the CCPI — too small a share of
 * emissions to rank, e.g. Panama — contributes 0 rather than being penalised
 * for its absence.
 */
const POLLUTION_POINTS = (index: number): number => (index < 35 ? 1 : index <= 60 ? 0 : -1);
const CRI_COUNTRIES = 174;
const CRI_POINTS = (rank: number): number => (rank <= CRI_COUNTRIES / 3 ? -1 : rank <= (2 * CRI_COUNTRIES) / 3 ? 0 : 1);
const CCPI_COUNTRIES = 67;
const CCPI_POINTS = (rank: number | undefined): number =>
  rank === undefined ? 0 : rank <= CCPI_COUNTRIES / 3 ? 1 : rank <= (2 * CCPI_COUNTRIES) / 3 ? 0 : -1;

export function climateScore(c: ClimateFactor): number {
  return POLLUTION_POINTS(c.pollutionIndex) + CRI_POINTS(c.climateRiskRank) + CCPI_POINTS(c.ccpiRank);
}

export function climateRating(c: ClimateFactor): Rating {
  const points = climateScore(c);
  if (points >= 2) return 'favorable';
  if (points <= -2) return 'unfavorable';
  return 'neutral';
}

/**
 * Three independent -1/0/+1 signals, each split into thirds of its own
 * scale and added together, the same shape as climate: everyday crime
 * (Numbeo's Crime Index, 0-100, lower is better), organised crime (the
 * Global Organized Crime Index criminality score, 1-10, lower is better) and
 * corruption (Transparency International's Corruption Perceptions Index,
 * 0-100, higher is cleaner).
 */
const CRIME_POINTS = (index: number): number => (index < 100 / 3 ? 1 : index <= 200 / 3 ? 0 : -1);
const ORGANISED_CRIME_POINTS = (score: number): number => (score <= 4 ? 1 : score <= 7 ? 0 : -1);
const CORRUPTION_POINTS = (index: number): number => (index >= 200 / 3 ? 1 : index >= 100 / 3 ? 0 : -1);

export function safetyScore(s: SafetyFactor): number {
  return CRIME_POINTS(s.crimeIndex) + ORGANISED_CRIME_POINTS(s.organisedCrimeScore) + CORRUPTION_POINTS(s.corruptionIndex);
}

export function safetyRating(s: SafetyFactor): Rating {
  const points = safetyScore(s);
  if (points >= 2) return 'favorable';
  if (points <= -2) return 'unfavorable';
  return 'neutral';
}

/** The rating of any scored factor, derived for healthcare, climate and safety, stored for the rest. */
export function ratingOf(country: CountryProfile, factor: ScoredFactor): Rating {
  if (factor === 'healthcare') return healthcareRating(country.factors.healthcare);
  if (factor === 'climate') return climateRating(country.factors.climate);
  if (factor === 'safety') return safetyRating(country.factors.safety);
  return country.factors[factor].rating;
}

export type Band = 'pack' | 'look' | 'think';

export const BAND_LABEL: Record<Band, string> = {
  pack: 'Pack your bags',
  look: 'Worth a look',
  think: 'Think twice',
};

/** Best first, which is also the order the columns appear in. */
export const BAND_ORDER: readonly Band[] = ['pack', 'look', 'think'];

/** 0–100, though in practice the curated set spans roughly 20–100. */
export function scoreOf(country: CountryProfile): number {
  return SCORED_FACTORS.reduce(
    (total, factor) => total + RATING_SHARE[ratingOf(country, factor)] * FACTOR_WEIGHTS[factor],
    0,
  );
}

export function bandOf(score: number): Band {
  if (score >= 70) return 'pack';
  if (score >= 50) return 'look';
  return 'think';
}

/**
 * True when no residency route exists for a retiree, so the country is not
 * somewhere you could move even if the rest of it appeals. The score already
 * sinks these, but it reads as "scored badly" rather than "you cannot go".
 */
export function hasNoRetirementRoute(country: CountryProfile): boolean {
  return country.factors.expatTaxTreatment.rating === 'unfavorable';
}
